import React, {
    Component,
    ReactElement
} from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import shallowEqual from 'shallowequal';

import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import {
    FilterValueType
} from '~/shared/src/validationSchemes/modules/overview';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {
    ToggleButton,
    ToggleButtonChangeEvent
} from 'primereact/togglebutton';
import {Panel} from 'primereact/panel';

import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';

import {createReactMouseEvent} from '~/frontend/src/utilities/reactHelpers';
import {StoreState} from '~/frontend/src/store';


export type DragElement = HTMLElement & {
    sorterElement : HTMLElement;
    sorterIndex : number;
    originalSorterIndex : number;
    offsetX : number;
    offsetY : number;
};

export type ColumnSortMenuColumnSorter = {
    key : string;
    field : string;
    direction : SortDirection;
};

export type ColumnSortMenuSingleColumn = {
    key : string;
    field : string;
    header? : string | JSX.Element;
    hidden : boolean;
    hidable : boolean;
    valueType : FilterValueType;
};

export type ColumnSortMenuColumnsGroup = {
    key? : string;
    label : string | JSX.Element;
    showHeader? : boolean;
    columns : ColumnSortMenuSingleColumn[]
};

export type ColumnSortMenuColumn = ColumnSortMenuSingleColumn | ColumnSortMenuColumnsGroup;

type DefaultProps = {
    disabled? : boolean;
    labeled? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    disabled: false,
    labeled : false
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id : string;

    /**
     * An array of columnsGroup objects for all columns with their visibility and props.
     */
    columns : ColumnSortMenuColumn[];

    /**
     * An array of column sorters.
     */
    sorters : ColumnSortMenuColumnSorter[];

    /**
     * Callback to invoke on sorters change.
     *
     * @param {ColumnSortMenuColumnSorter[]} sorters
     */
    onChange? : ( sorters : ColumnSortMenuColumnSorter[] ) => void;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    tabIndex? : number;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props & StateProps;

type RenderColumn = {
    label : string | JSX.Element;
    field : string;
    valueType : FilterValueType;
} & object;

type RenderColumnsGroup = {
    label : string | JSX.Element;
    code : string;
    items : RenderColumn[];
};

type RenderColumns = ( RenderColumn | RenderColumnsGroup )[];

type State = {
    columns : RenderColumns;
    sorters : ColumnSortMenuColumnSorter[];
}

/**
 * ColumnSortMenu
 */
class ColumnSortMenu extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    contentButton? : ContentButtonType | null;
    justInteracted : boolean = false;

    dragElement? : DragElement;
    boundGlobalMouseMoveHandler? : ( event : MouseEvent ) => void;
    boundGlobalMouseUpHandler? : ( event : MouseEvent ) => void;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            columns: [],
            sorters: props.sorters
        };

        // bind event handlers
        this.onApplyButtonClick = this.onApplyButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onContentButtonCollapse = this.onContentButtonCollapse.bind( this );
        this.onAddSorter = this.onAddSorter.bind( this );
        this.onDragStart = this.onDragStart.bind( this );
        this.onDragMove = this.onDragMove.bind( this );
        this.onDragEnd = this.onDragEnd.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : Props, state : State ) : Partial<State> {
        const retVal          = {
                  columns: [] as RenderColumns
              },
              hasColumnGroups = ( props.columns?.[ 0 ] as unknown as ColumnSortMenuColumnsGroup )?.columns !== undefined;

        if ( hasColumnGroups ) {
            const columnGroups = props.columns as unknown as ColumnSortMenuColumnsGroup[];

            let columnGroupCount = 0;

            for ( const columnGroup of columnGroups ) {
                const groupCode                     = columnGroup.key ?? String( columnGroupCount ),
                      groupColumns : RenderColumn[] = [];

                // group columns
                for ( const column of columnGroup.columns ) {
                    if ( !column.hidden ) {
                        groupColumns.push( {
                            label: column.header ?? column.field,
                            ...column
                        } );
                    }
                }

                if ( groupColumns.length ) {
                    // add with group header
                    if ( columnGroup.showHeader ?? true ) {
                        retVal.columns.push( {
                            label: columnGroup.label,
                            code : groupCode,
                            items: groupColumns
                        } as RenderColumnsGroup );
                    }
                    // add without group header
                    else {
                        retVal.columns = retVal.columns.concat( groupColumns );
                    }
                }

                columnGroupCount++;
            }
        }
        else {
            const columns                       = props.columns as unknown as ColumnSortMenuSingleColumn[],
                  groupColumns : RenderColumn[] = [];

            for ( const column of columns ) {
                if ( !column.hidden ) {
                    groupColumns.push( {
                        label: column.header ?? column.field,
                        ...column
                    } );
                }
            }
            retVal.columns = groupColumns;
        }

        if ( !_.isEqual( state.columns, retVal.columns ) ) {
            return retVal;
        }
        else {
            return {};
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : Props ) {
        if ( nextProps.sorters !== this.props.sorters ) {
            this.setState( { sorters: nextProps.sorters } );
            return false;
        }

        return true;
    }

    /**
     * Handler for apply button click
     *
     * @private
     */
    onApplyButtonClick( event : React.MouseEvent ) {
        this.justInteracted = false;

        if ( !shallowEqual( this.props.sorters, this.state.sorters ) ) {
            this.props.onChange?.( this.state.sorters );
        }

        this.contentButton?.collapse( event );
    }

    /**
     * Handler for cancel button click
     *
     * @private
     */
    onCancelButtonClick( event : React.MouseEvent ) {
        this.justInteracted = false;

        this.contentButton?.collapse( event );
    }

    onContentButtonCollapse( event : React.MouseEvent, isOutsideClicked : boolean, eventPhase : 'beforeClick' | 'afterClick' | 'collapse' ) {
        const retVal = !this.justInteracted && isOutsideClicked;

        this.justInteracted = false;

        if ( retVal && eventPhase !== 'beforeClick' ) {
            this.setState( {
                columns: [],
                sorters: this.props.sorters
            } );
        }

        return retVal;
    }

    /**
     * Handler for add sorter.
     *
     * @param event
     *
     * @private
     */
    onAddSorter( event : DropdownChangeEvent ) {
        const sorters : ColumnSortMenuColumnSorter[] = [].concat( this.state.sorters as [] );

        this.justInteracted = true;

        sorters.push( {
            key      : event.value.key,
            field    : event.value.field,
            direction: 'ASC' as SortDirection
        } );

        this.setState( { sorters } );
    }

    /**
     * Handler for sorter remove.
     *
     * @param {Object} sorter
     *
     * @private
     */
    onRemoveSorter( sorter : ColumnSortMenuColumnSorter ) {
        const sorters = [].concat( this.state.sorters as [] );

        this.justInteracted = true;

        for ( let i = 0; i < sorters.length; i++ ) {
            if ( sorters[ i ] === sorter ) {
                sorters.splice( i, 1 );
                break;
            }
        }

        this.setState( { sorters } );
    }

    /**
     * Handler for sorter field change.
     *
     * @param {Object} sorter
     * @param event
     *
     * @private
     */
    onSorterFieldChange( sorter : ColumnSortMenuColumnSorter, event : DropdownChangeEvent ) {
        this.justInteracted = true;

        if ( sorter.field !== event.value.field ) {
            const sorters : ColumnSortMenuColumnSorter[] = [].concat( this.state.sorters as [] );

            for ( let i = 0; i < sorters.length; i++ ) {
                if ( sorter === sorters[ i ] ) {
                    sorters[ i ] = {
                        key      : event.value.key,
                        field    : event.value.field,
                        direction: sorter.direction
                    };
                    break;
                }
            }

            this.setState( { sorters } );
        }
    }

    /**
     * Handler for sorter direction change.
     *
     * @param {Object} sorter
     * @param {'ASC'|'DESC'} direction
     * @param event
     *
     * @private
     */
    onSorterDirectionChange( sorter : ColumnSortMenuColumnSorter, direction : SortDirection, event : ToggleButtonChangeEvent ) {
        this.justInteracted = true;

        if ( sorter.direction !== direction ) {
            const sorters : ColumnSortMenuColumnSorter[] = [].concat( this.state.sorters as [] );

            for ( let i = 0; i < sorters.length; i++ ) {
                if ( sorter === sorters[ i ] ) {
                    sorters[ i ] = {
                        key  : sorters[ i ].key,
                        field: sorters[ i ].field,
                        direction
                    };
                    break;
                }
            }

            this.setState( { sorters } );
        }
    }

    /**
     * Handler for sorter drag start.
     *
     * @param event
     *
     * @private
     */
    onDragStart( event : React.MouseEvent ) {
        const nativeEvent      = event.nativeEvent,
              sorterElement    = ( event.target as HTMLElement ).parentNode as HTMLElement,
              sorterIndex      = parseInt( sorterElement.id.substr( sorterElement.id.lastIndexOf( '_' ) + 1 ) ),
              sorterElementBox = sorterElement.getBoundingClientRect();

        if ( sorterElement ) {
            // create drag clone
            this.dragElement = sorterElement.cloneNode( true ) as DragElement;
            this.dragElement.id = `${this.props.uid}_sorter_${sorterIndex}_clone`;
            this.dragElement.sorterElement = sorterElement;
            this.dragElement.sorterIndex = sorterIndex;
            this.dragElement.originalSorterIndex = sorterIndex;
            this.dragElement.offsetX = nativeEvent.pageX - sorterElementBox.x;
            this.dragElement.offsetY = nativeEvent.pageY - sorterElementBox.y;

            this.dragElement.classList.add( 'p-component' );
            this.dragElement.classList.add( 'yn-drag-item' );

            this.dragElement.style.left = `calc( ${nativeEvent.pageX - this.dragElement.offsetX}px - .5rem)`;
            this.dragElement.style.top = ( nativeEvent.pageY - this.dragElement.offsetY ) + 'px';

            document.body.appendChild( this.dragElement );

            // create global mouse move handler
            this.boundGlobalMouseMoveHandler = ( e : MouseEvent ) => {
                this.onDragMove( createReactMouseEvent( e, this.dragElement! ) );
            }
            document.querySelector( 'html' )?.addEventListener( 'mousemove', this.boundGlobalMouseMoveHandler );

            // create global mouseup handler
            this.boundGlobalMouseUpHandler = ( e : MouseEvent ) => {
                this.onDragEnd( createReactMouseEvent( e, this.dragElement! ) );
            }
            document.querySelector( 'html' )?.addEventListener( 'mouseup', this.boundGlobalMouseUpHandler );

            // set original to invisible
            this.dragElement.sorterElement.classList.add( 'yn-drag-placeholder' );

            nativeEvent.preventDefault();
        }
    }

    /**
     * Handler for sorter drag move.
     *
     * @param event
     *
     * @private
     */
    onDragMove( event : React.MouseEvent ) {
        if ( this.dragElement ) {
            const nativeEvent      = event.nativeEvent,
                  parentElement    = this.dragElement.sorterElement.parentNode as HTMLElement,
                  parentElementBox = parentElement?.getBoundingClientRect(),
                  sorterElements   = parentElement?.querySelectorAll( '.yn-sorter' ),
                  oldY             = parseInt( this.dragElement.style.top ),
                  //x                = Math.round( nativeEvent.pageX - this.dragElement.offsetX ),
                  y                = Math.round( nativeEvent.pageY - this.dragElement.offsetY );

            // set position
            //this.dragElement.style.left = x + 'px';
            this.dragElement.style.top = y + 'px';

            // position within parent
            if (
                /*parentElementBox.x < nativeEvent.pageX && nativeEvent.pageX < parentElementBox.x + parentElementBox.width
                &&*/ parentElementBox.y < nativeEvent.pageY && nativeEvent.pageY < parentElementBox.y + parentElementBox.height
            ) {

                // moved up or down from outside into parent
                if ( y < oldY ) {

                    // has to change position with a sorter before?
                    for ( let sorterIndex = 0; sorterIndex < this.dragElement.sorterIndex; sorterIndex++ ) {
                        const sorterElementBox = sorterElements[ sorterIndex ].getBoundingClientRect();

                        if ( y < sorterElementBox.y + ( sorterElementBox.height / 2 ) ) {
                            parentElement.removeChild( this.dragElement.sorterElement );

                            parentElement.insertBefore( this.dragElement.sorterElement, sorterElements[ sorterIndex ] );

                            this.dragElement.sorterIndex = sorterIndex;

                            break;
                        }
                    }
                }

                // move down?
                else if ( y > oldY ) {

                    // has to change position with a sorter after?
                    for ( let sorterIndex = sorterElements.length - 1; sorterIndex > this.dragElement.sorterIndex; sorterIndex-- ) {
                        const sorterElementBox = sorterElements[ sorterIndex ].getBoundingClientRect();

                        if ( y > sorterElementBox.y - sorterElementBox.height ) {
                            parentElement.removeChild( this.dragElement.sorterElement );

                            if ( sorterIndex < sorterElements.length - 1 ) {
                                parentElement.insertBefore( this.dragElement.sorterElement, sorterElements[ sorterIndex ].nextSibling );
                            }
                            else {
                                parentElement.appendChild( this.dragElement.sorterElement );
                            }

                            this.dragElement.sorterIndex = sorterIndex;

                            break;
                        }
                    }
                }
            }

            nativeEvent.preventDefault();
        }
    }

    /**
     * Handler for sorter drag end.
     *
     * @param event
     *
     * @private
     */
    onDragEnd( event : React.MouseEvent ) {
        const nativeEvent      = event.nativeEvent,
              parentElement    = this.dragElement?.sorterElement.parentNode as HTMLElement,
              parentElementBox = parentElement.getBoundingClientRect(),
              sorterElements   = parentElement.querySelectorAll( '.yn-sorter' );

        // cleanup global mouse move handler
        if ( this.boundGlobalMouseMoveHandler ) {
            document.querySelector( 'html' )?.removeEventListener( 'mousemove', this.boundGlobalMouseMoveHandler );
            delete this.boundGlobalMouseMoveHandler;
        }

        // cleanup global mouseup handler
        if ( this.boundGlobalMouseUpHandler ) {
            document.querySelector( 'html' )?.removeEventListener( 'mouseup', this.boundGlobalMouseUpHandler );
            delete this.boundGlobalMouseUpHandler;
        }

        // place at original position if dropped outside parent and not there
        if (
            (
                nativeEvent.pageX < parentElementBox.x
                || nativeEvent.pageX > parentElementBox.x + parentElementBox.width
                || nativeEvent.pageY < parentElementBox.y
                || nativeEvent.pageY > parentElementBox.y + parentElementBox.height
            )
            && sorterElements[ this.dragElement!.originalSorterIndex ] !== this.dragElement!.sorterElement
        ) {
            parentElement.removeChild( this.dragElement!.sorterElement );

            if ( this.dragElement!.originalSorterIndex < sorterElements.length - 1 ) {
                parentElement.insertBefore( this.dragElement!.sorterElement, sorterElements[ this.dragElement!.originalSorterIndex ] );
            }
            else {
                parentElement.appendChild( this.dragElement!.sorterElement );
            }
        }

        // reorder sorters
        if ( this.dragElement!.originalSorterIndex !== this.dragElement!.sorterIndex ) {
            const sorters : ColumnSortMenuColumnSorter[] = [].concat( this.state.sorters as [] ),
                  [ sorter ]                             = sorters.splice( this.dragElement!.originalSorterIndex, 1 );

            sorters.splice( this.dragElement!.sorterIndex, 0, sorter );

            this.setState( { sorters } );
        }

        // set original to visible
        if ( this.dragElement!.sorterElement ) {
            this.dragElement!.sorterElement.classList.remove( 'yn-drag-placeholder' );
        }

        // cleanup dragElement
        if ( this.dragElement ) {
            document.body.removeChild( this.dragElement );
            delete this.dragElement;
        }
    }

    /**
     * Builds the sorter elements.
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getColumnSorters() : ReactElement {
        const sorterElements : ReactElement[] = [],
              sorters                         = this.state.sorters,
              hasColumnGroups                 = ( this.props.columns?.[ 0 ] as unknown as ColumnSortMenuColumnsGroup )?.columns !== undefined;

        let sorterIndex = 0;

        for ( const sorter of sorters ) {
            const sorterLabels = [ '', '' ];

            // get column for sorter
            let sorterColumn;
            if ( hasColumnGroups ) {
                const columnGroups : RenderColumnsGroup[] = [].concat( this.state.columns as [] );

                outerFor: for ( const columnGroup of columnGroups ) {
                    for ( const column of columnGroup.items ) {
                        if ( column.field === sorter.field ) {
                            sorterColumn = column;
                            break outerFor;
                        }
                    }
                }
            }
            else {
                const columns : RenderColumn[] = [].concat( this.state.columns as [] );

                for ( const column of columns ) {
                    if ( column.field === sorter.field ) {
                        sorterColumn = column;
                        break;
                    }
                }
            }

            // build sorter DropDown options by removing all other sorters from columns
            let availableColumns : RenderColumns = [];
            if ( hasColumnGroups ) {
                const columnGroups : RenderColumnsGroup[] = [].concat( this.state.columns as [] );

                for ( const sorterToRemove of sorters ) {

                    outerFor: for ( let i = 0; i < columnGroups.length; i++ ) {
                        columnGroups[ i ] = Object.assign( {}, columnGroups[ i ] );
                        columnGroups[ i ].items = [].concat( columnGroups[ i ].items as [] );

                        for ( let k = 0; k < columnGroups[ i ].items.length; k++ ) {
                            if ( columnGroups[ i ].items[ k ].field !== sorter.field && columnGroups[ i ].items[ k ].field === sorterToRemove.field ) {
                                columnGroups[ i ].items.splice( k, 1 );

                                if ( !columnGroups[ i ].items.length ) {
                                    columnGroups.splice( i, 1 );
                                }
                                break outerFor;
                            }
                        }
                    }

                    availableColumns = columnGroups;
                }
            }
            else {
                const columns : RenderColumn[] = [].concat( this.state.columns as [] );

                for ( const sorterToRemove of sorters ) {

                    for ( let i = 0; i < columns.length; i++ ) {
                        if ( columns[ i ].field !== sorter.field && columns[ i ].field === sorterToRemove.field ) {
                            columns.splice( i, 1 );
                            break;
                        }
                    }

                    availableColumns = columns;
                }
            }

            if ( sorterColumn ) {
                switch ( sorterColumn.valueType ) {
                    case 'BOOLEAN':
                        sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.boolean' );
                        sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.boolean' );
                        break;

                    case 'DATE':
                    case 'TIME':
                    case 'TIMESTAMP':
                        sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.datetime' );
                        sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.datetime' );
                        break;

                    case 'DOUBLE':
                    case 'INTEGER':
                        sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.numeric' );
                        sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.numeric' );
                        break;

                    default:
                        sorterLabels[ 0 ] = __( 'YN.base.columnSort.sort.asc.alpha' );
                        sorterLabels[ 1 ] = __( 'YN.base.columnSort.sort.desc.alpha' );
                }

                sorterElements.push(
                    <div
                        key={`${this.props.uid}_sorter_${Math.random()}`}
                        id={`${this.props.uid}_sorter_${sorterIndex}`}
                        className="flex align-items-center mb-2 yn-sorter"
                    >
                        <Button
                            onClick={this.onRemoveSorter.bind( this, sorter )}
                            icon="pi pi-times"
                            className="p-button-rounded p-button-danger p-button-text"
                        />
                        <div className="inline ml-2 yn-sort-label">
                            {sorterIndex > 0
                                ? __( 'YN.base.columnSort.sort.other.label' )
                                : __( 'YN.base.columnSort.sort.first.label' )}
                        </div>
                        <Dropdown
                            value={sorterColumn}
                            options={availableColumns}
                            onChange={this.onSorterFieldChange.bind( this, sorter )}
                            optionLabel="label"
                            optionGroupLabel={hasColumnGroups ? 'label' : undefined}
                            optionGroupChildren={hasColumnGroups ? 'items' : undefined}
                            className="ml-2"
                            style={{ width: '18rem' }}
                            appendTo={document.body}
                        />
                        <div className="inline ml-2" style={{ whiteSpace: 'nowrap' }}>
                            <ToggleButton
                                checked={sorter.direction === 'ASC'}
                                onChange={this.onSorterDirectionChange.bind( this, sorter, 'ASC' )}
                                onLabel={sorterLabels[ 0 ]}
                                offLabel={sorterLabels[ 0 ]}
                                className="yn-toggle-button-switch-left"
                            />
                            <ToggleButton
                                checked={sorter.direction === 'DESC'}
                                onChange={this.onSorterDirectionChange.bind( this, sorter, 'DESC' )}
                                onLabel={sorterLabels[ 1 ]}
                                offLabel={sorterLabels[ 1 ]}
                                className="yn-toggle-button-switch-right"
                            />
                        </div>
                        <div
                            className={`yn-change-order-button ml-2 ${sorters.length === 1 ? 'p-disabled' : ''}`}
                            tabIndex={0}
                            onMouseDown={this.onDragStart}
                            onMouseUp={this.onDragEnd}
                        ></div>
                    </div>
                );
            }

            sorterIndex++;
        }

        return <div className="yn-sorter-container">{sorterElements}</div>;
    }

    /**
     * Builds the add sorter field.
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getAddSorterField() : ReactElement {
        const sorters         = this.state.sorters,
              hasColumnGroups = ( this.props.columns?.[ 0 ] as unknown as ColumnSortMenuColumnsGroup )?.columns !== undefined;

        // build sorter DropDown options by removing all sorters from columns
        let availableColumns : RenderColumns = [];
        if ( hasColumnGroups ) {
            const columnGroups : RenderColumnsGroup[] = [].concat( this.state.columns as [] );

            for ( const sorterToRemove of sorters ) {

                outerFor: for ( let i = 0; i < columnGroups.length; i++ ) {
                    columnGroups[ i ] = Object.assign( {}, columnGroups[ i ] );
                    columnGroups[ i ].items = [].concat( columnGroups[ i ].items as [] );

                    for ( let k = 0; k < columnGroups[ i ].items.length; k++ ) {
                        if ( columnGroups[ i ].items[ k ].field === sorterToRemove.field ) {
                            columnGroups[ i ].items.splice( k, 1 );

                            if ( !columnGroups[ i ].items.length ) {
                                columnGroups.splice( i, 1 );
                            }
                            break outerFor;
                        }
                    }
                }
            }

            availableColumns = columnGroups;
        }
        else {
            const columns : RenderColumn[] = [].concat( this.state.columns as [] );

            for ( const sorterToRemove of sorters ) {

                for ( let i = 0; i < columns.length; i++ ) {
                    if ( columns[ i ].field === sorterToRemove.field ) {
                        columns.splice( i, 1 );
                        break;
                    }
                }
            }

            availableColumns = columns;
        }

        let disabled    = false,
            placeholder = __( 'YN.base.columnSort.addSorter.placeholder' );

        if ( !availableColumns.length ) {
            disabled = true;
            placeholder = __( 'YN.base.columnSort.addSorter.noMoreColumns' );
        }

        return <Dropdown
            options={availableColumns}
            onChange={this.onAddSorter}
            optionLabel="label"
            optionGroupLabel={hasColumnGroups ? 'label' : undefined}
            optionGroupChildren={hasColumnGroups ? 'items' : undefined}
            className="ml-2"
            style={{ width: '30rem' }}
            disabled={disabled}
            placeholder={placeholder}
            appendTo={document.body}
        />;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  id,
                  className,
                  disabled,
                  labeled,
                  tabIndex
              } = this.props;

        // TODO: overlay content layout not correct

        return <ContentButton
            id={id}

            ref={element => this.contentButton = element}

            horizontalContentAlign="right"
            verticalContentAlign="bottom"
            rounded={labeled
                ? false
                : true}
            text={true}
            label={labeled
                ? __( 'YN.base.columnSort.label' )
                : undefined}
            icon="pi pi-sort-alt"
            tooltip={__( 'YN.base.columnSort.button' )}
            tooltipOptions={config.frontend?.buttonTooltipOptions}
            className={`yn-toolbar-button yn-option-button yn-sort-option-button ${className}`}
            contentClassName="yn-contentbutton-overlay-content-page-menu"
            tabIndex={tabIndex}

            disabled={disabled}

            onCollapse={this.onContentButtonCollapse}

            content={
                <Panel
                    style={{
                        width: '45rem'
                    }}
                    pt={{
                        content: {
                            style: {
                                border  : 'none',
                                position: 'relative'
                            }
                        }
                    }}
                >
                    {!labeled &&
                        <label
                            key={`${uid}_header`}

                            className="yn-menu-title-label"
                        >
                            {__( 'YN.base.columnSort.label' )}
                        </label>
                    }

                    {this.getColumnSorters()}

                    <hr className="mt-3 mb-3 yn-ruler"/>

                    {this.getAddSorterField()}

                    <hr className="mt-3 mb-3 yn-ruler"/>

                    <div className="flex justify-content-end">
                        <Button
                            label={__( 'YN.base.common.apply' )}
                            onClick={this.onApplyButtonClick}
                            className="mr-3 p-button-outlined yn-button-defaultwidth text-center"
                        />
                        <Button
                            label={__( 'YN.base.common.cancel' )}
                            onClick={this.onCancelButtonClick}
                            className="p-button-secondary p-button-outlined yn-button-defaultwidth text-center"
                        />
                    </div>
                </Panel>
            }
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect( mapStateToProps, null )( ColumnSortMenu );
